/**
 * PATH: packages/bills/src/components/avatar-tint.ts
 * FILE_ID: BILLS-PKG-CMP-AVATAR-TINT-001
 * PURPOSE: Deterministically map a vendor name → a soft tint class set and its
 *          monogram initial, so the bills-list avatars get tasteful, stable
 *          per-vendor colour variety (light + dark) like the approved mockup.
 *
 * WHY: A monogram avatar with a *stable* colour per vendor is a cheap, strong
 *      recognition cue in a dense triage list — the eye learns "Stripe is the
 *      violet S". Determinism means the same vendor always maps to the same
 *      colour across renders, pages, and sessions (no flicker on refetch).
 *
 * FLOW:
 *   ┌────────────┐   ┌───────────────┐   ┌────────────────────────┐
 *   │ vendorName │ → │ fnv-ish hash  │ → │ tint class string       │
 *   │ (string)   │   │ % TINTS.len   │   │ + monogram initial      │
 *   └────────────┘   └───────────────┘   └────────────────────────┘
 *
 * DEPENDENCIES: none — pure module, no React, no DOM.
 *
 * RELATED:
 *   - BillsListPanel.tsx (consumer — renders the monogram avatar)
 */

/**
 * Six soft tints, each with a light + dark pairing that sits on a rounded
 * monogram chip. Order is stable — do not reorder, the hash indexes into it.
 */
export const AVATAR_TINTS = [
  'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300',
  'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
  'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300',
  'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300',
  'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300',
] as const;

/** Neutral fallback used when there is no vendor name to key off. */
export const AVATAR_TINT_FALLBACK = 'bg-muted text-muted-foreground';

/**
 * Map a vendor name → one of AVATAR_TINTS. Deterministic (same name in → same
 * class out) and case/whitespace-insensitive so "Stripe" and " stripe " land
 * on the same colour. Empty/absent names return the neutral fallback.
 *
 * pure: (string | null | undefined) → string
 */
export function avatarTint(name: string | null | undefined): string {
  const key = (name ?? '').trim().toLowerCase();
  if (!key) return AVATAR_TINT_FALLBACK;
  // Small FNV-1a-style rolling hash — cheap, stable, and well-spread for the
  // short strings (vendor names) we key on. `| 0` keeps it a 32-bit int.
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(hash) % AVATAR_TINTS.length;
  return AVATAR_TINTS[idx];
}

/**
 * The monogram initial for a vendor — the first non-whitespace character,
 * upper-cased. Falls back to "?" for empty/absent names so the avatar chip is
 * never blank.
 *
 * pure: (string | null | undefined) → string
 */
export function avatarInitial(name: string | null | undefined): string {
  const key = (name ?? '').trim();
  if (!key) return '?';
  return key[0].toUpperCase();
}
